import "firebase/compat/auth";
import "firebase/compat/firestore";
import { useEffect, useMemo, useRef, useState } from "react";
import { IoNotifications } from "react-icons/io5";
import { useSelector } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Spinner, Table } from "reactstrap";
import { searchDistributorProducts } from "../../api/edi.api";
import navbarLogo from "../../assets/imgs/techmicro-logo.png";
import { routes } from "../../routes";
import ProfileDropdown from "./ProfileDropdown";
import { setThemeColor } from "../../store/actions/actions";
import { useDispatch } from "react-redux";
import { syncPermissions } from "../../utils/permissions";

const items = [
  { key: "1", label: "Profile", href: "/admin/user-profile-page", show: ["admin", "user"] },
  { key: "2", label: "User Management Settings", href: "/admin/settings", show: ["admin"], permissionName: "Settings" },
  { key: "3", label: "System Settings", href: "/admin/system-settings", show: ["admin"], permissionName: "Settings" },
  { key: "4", label: "Categories", href: "/admin/categories", show: ["admin"] },
  { key: "5", label: "Sub Categories", href: "/admin/sub-categories", show: ["admin"] },
  { key: "5_1", label: "Product Variant/Groups", href: "/admin/product-groups", show: ["admin"] },
  { key: "6", label: "Allowed Manufacturers", href: "/admin/allowed-manufacturers", show: ["admin"] },
  { key: "7", label: "DNH Jobs", href: "/admin/dnh-jobs", show: ["admin"] },
  { key: "8", label: "Reviews", href: "/admin/reviews", show: ["admin",] },
];

const NavBar = () => {
  const { all_products } = useSelector((state) => state.products);
  const { user } = useSelector((state) => state.auth);
  const { themeColor, notifications } = useSelector((state) => state.redux);
  const dispatch = useDispatch();

  const unreadCount = useMemo(() => {
    if (!notifications || !Array.isArray(notifications)) return 0;
    const isAdmin = user?.role === "Admin" || user?.role === "Super-admin";
    const filtered = isAdmin
      ? notifications.filter((i) => i?.type === "user" || i?.targetRole === "admin" || i?.targetRole === "all" || !i?.type)
      : notifications.filter((i) => (i?.type === "admin" && i?.userId === user?._id) || i?.userId === user?._id);
    return filtered.filter((i) => !i.read).length;
  }, [notifications, user]);

  const searchRef = useRef(null);
  const history = useHistory();
  const { pathname } = useLocation();

  const [duplicateProducts, setDuplicateProducts] = useState([]);
  const [dropdown, setDropdown] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [distributorResults, setDistributorResults] = useState([]);
  const [distributorsList, setDistributorsList] = useState([]);
  const [manufacturerDistributors, setManufacturerDistributors] = useState([]);

  const [searchLoading, setSearchLoading] = useState(false);

  /** ---- Debounce search ---- */
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(handler);
  }, [search]);

  const isDebouncing = search !== debouncedSearch && search.trim().length > 0;
  const isLoading = isDebouncing || searchLoading;

  /** ---- ROUTE FILTER ---- */
  const getFilteredRoutes = () =>
    routes.filter((item) => {
      const currentUserRole = user?.role?.toLowerCase();
      if (currentUserRole === 'super-admin' || item.name === 'Profile') {
        if (item.isMenu) return item;
        return false;
      }
      const syncedPermissions = syncPermissions(user?.permissions);
      const permission = syncedPermissions.find((perm) => perm.name === item.name);
      if (item.isMenu) {
        return permission ? permission.value : false;
      }
      return false;
    });
  const filteredRoutes = getFilteredRoutes();

  /** ---- LOCAL SEARCH ---- */
  const filteredProducts = useMemo(() => {
    let text = debouncedSearch.trim().toUpperCase();
    return duplicateProducts.filter((item) => {
      const s = `${item?.id} ${item?.status} ${item?.description} ${item?.price} ${item?.name} ${item?.sku} ${item?.mfg_name} ${item?.vendorName}`;
      return s.toUpperCase().includes(text);
    });
  }, [debouncedSearch, duplicateProducts]);

  const groupedProducts = useMemo(() => {
    const grouped = filteredProducts.reduce((acc, product) => {
      const { mfg_name, stc_quantity } = product;
      const quantity = Number(stc_quantity) || 0;
      if (!acc[mfg_name]) acc[mfg_name] = { mfg_name, totalQuantity: 0, products: [] };
      acc[mfg_name].totalQuantity += quantity;
      acc[mfg_name].products.push(product);
      return acc;
    }, {});
    return Object.values(grouped);
  }, [filteredProducts]);

  const combinedResults = useMemo(() => {
    const distMapped = Array.isArray(distributorResults)
      ? distributorResults.map((d) => ({ vendor: d.distributor, part: d.partNumber, stock: d.quantity, price: d.price }))
      : [];
    const internalMapped = Array.isArray(groupedProducts)
      ? groupedProducts.map((group) => ({
        vendor: "Techmicro",
        part: group?.products?.[0]?.sku ? group.products[0].sku.slice(4) : "N/A",
        stock: group?.totalQuantity ?? 0,
        price: group?.products?.[0]?.price ?? "N/A",
      }))
      : [];
    return [...internalMapped, ...distMapped];
  }, [groupedProducts, distributorResults]);

  /** ---- SEARCH INPUT ---- */
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setDropdown(!!e.target.value);
  };

  /** ---- SET INITIAL PRODUCTS ---- */
  useEffect(() => {
    const filtered = all_products?.filter((i) => i?.stc_quantity > 0) ?? [];
    setDuplicateProducts(filtered);
  }, [all_products]);

  /** ---- CLOSE SEARCH DROPDOWN ON OUTSIDE CLICK ---- */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearch("");
        setDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /** ---- DISTRIBUTOR API (debounced) ---- */
  useEffect(() => {
    if (debouncedSearch) {
      setSearchLoading(true);
      searchDistributorProducts(debouncedSearch, (result) => {
        if (result?.status === 200) {
          setDistributorResults(result.data?.data ?? []);

          const techMicroItem = result.data?.data?.find(item => item.distributor === "TechMicro");
          setDistributorsList(techMicroItem?.distributors ?? []);
          setManufacturerDistributors(result.data?.manufacturerDistributors ?? []);
        } else {
          setDistributorResults([]);
          setDistributorsList([]);
          setManufacturerDistributors([]);
        }
        setSearchLoading(false);
      });
    } else {
      setDistributorResults([]);
      setDistributorsList([]);
      setManufacturerDistributors([]);
      setSearchLoading(false);
    }
  }, [debouncedSearch]);

  const handleProfileDropdownToggle = () => setDropdownOpen(!dropdownOpen);
  const handleNotifications = async () => history.push("/admin/notifications");

  const handleNavLinkClick = () => {
    setSearch("");
    localStorage.setItem("saleOrderPage", 1);
    localStorage.setItem("search", "");
    localStorage.setItem("purchaseOrderPage", 1);
    localStorage.setItem("purchaseSearch", "");
    localStorage.setItem("productPage", 1);
    localStorage.setItem("quotePage", 1);
    localStorage.setItem("quoteSearch", "");
    localStorage.setItem("customerPage", 1);
    localStorage.setItem("customerSearch", "");
    localStorage.setItem("vendorPage", 1);
    localStorage.setItem("vendorSearch", "");
    localStorage.setItem("manufacturersPage", 1);
    localStorage.setItem("mfgSearch", "");
    localStorage.setItem("selectedOrderStatus", "New");
  };

  return (
    <>
      <style>{`
        .custom-navbar-link {
          color: rgba(255, 255, 255, 0.85) !important;
          font-weight: 500;
          border-bottom: 2px solid transparent;
          padding: 6px 12px;
          margin: 0;
          border-radius: 6px 6px 0 0;
          transition: all 0.3s ease;
          font-size: 13px;
          display: inline-block;
          text-decoration: none;
          white-space: nowrap !important;
        }
        .custom-navbar-link:hover {
          color: #ffffff !important;
          background-color: rgba(255, 255, 255, 0.08);
        }
        .custom-navbar-link.active-link {
          color: #ffffff !important;
          font-weight: 700;
          border-bottom: 2px solid #ff4d4f;
          background-color: rgba(255, 77, 79, 0.15);
        }
       

        /* Tight desktop viewports (1200px - 1550px) */
        @media (max-width: 1550px) {
          .custom-navbar-link {
            font-size: 11px !important;
            padding: 5px 6px !important;
            margin: 0 1px !important;
          }
          
          .navbar-search-dropdown {
            width: 360px !important;
          }
          .navbar-nav {
            flex-wrap: nowrap !important;
            overflow-x: auto;
          }
        }

        /* Tighter laptop viewports (1200px - 1400px) */
        @media (max-width: 1400px) {
          .custom-navbar-link {
            font-size: 10px !important;
            padding: 4px 4px !important;
            margin: 0 !important;
          }
          .nav-link-divider {
            display: none !important;
          }
        }

        /* Collapsed menu styling below 1200px */
        @media (max-width: 1199px) {
          .navbar-collapse {
            background: rgba(24, 40, 72, 0.98);
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
            border-radius: 12px;
            padding: 16px 20px;
            margin-top: 12px;
            border: 1px solid rgba(255, 255, 255, 0.1);
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
          }
          .navbar-nav {
            align-items: stretch !important;
            width: 100%;
            margin: 8px 0 !important;
            padding: 0;
          }
          .custom-navbar-link {
            display: block;
            width: 100%;
            border-bottom: none !important;
            border-left: 3px solid transparent;
            border-radius: 0 6px 6px 0;
            margin: 4px 0 !important;
            padding: 8px 16px;
          }
          .custom-navbar-link.active-link {
            border-left: 3px solid #ff4d4f;
            background-color: rgba(255, 77, 79, 0.12);
          }
          .navbar-right-side {
            flex-direction: column;
            align-items: stretch !important;
            width: 100%;
            margin-top: 12px;
            padding-top: 12px;
            border-top: 1px solid rgba(255, 255, 255, 0.1);
            gap: 12px !important;
          }
          .navbar-search-wrapper {
            width: 100%;
          }
          .navbar-search-form {
            width: 100%;
          }
          .navbar-search-input {
            width: 100% !important;
          }
          .navbar-search-dropdown {
            width: 100% !important;
            left: 0 !important;
            right: 0 !important;
          }
          .navbar-action-icons {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            margin-top: 8px;
          }
        }
      `}</style>

      <nav
        className="navbar navbar-expand-xl navbar-dark shadow-sm mb-5"
        style={{
          background: "linear-gradient(135deg, #00509d 0%, #182848 100%)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <div className="container-fluid">
          {/* Logo */}
          <div className="me-3">
            <img src={navbarLogo} height={36} className="rounded-0" alt="logo" style={{ objectFit: 'contain' }} />
          </div>

          {/* Mobile toggler */}
          <button
            className="navbar-toggler border-0"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarSupportedContent"
            aria-controls="navbarSupportedContent"
            aria-expanded="false"
            aria-label="Toggle navigation"
            style={{ color: "rgba(255,255,255,0.8)" }}
          >
            <span className="navbar-toggler-icon" />
          </button>

          <div className="collapse navbar-collapse" id="navbarSupportedContent">
            {/* ---- Nav Routes ---- */}
            <ul className="navbar-nav mx-2 me-auto mb-2 mb-xl-0 d-flex align-items-center" style={{ flexWrap: 'nowrap' }}>
              {filteredRoutes?.map((i, idx) => {
                const isActive = pathname.includes(i?.path);
                return (
                  <div key={idx} className="d-flex align-items-center">
                    <Link
                      className={`custom-navbar-link ${isActive ? "active-link" : ""}`}
                      onClick={handleNavLinkClick}
                      to={i.layout + i.path}
                    >
                      {i?.name}
                    </Link>
                    {idx < filteredRoutes.length - 1 && (
                      <div className="nav-link-divider" style={{ width: '1px', height: '16px', backgroundColor: 'rgba(255,255,255,0.2)', margin: '0 4px' }} />
                    )}
                  </div>
                );
              })}
            </ul>

            {/* ---- Right Side ---- */}
            <div className="d-flex gap-3 align-items-center navbar-right-side">

              {/* ---- Search ---- */}
              <div ref={searchRef} style={{ position: "relative" }}>
                <form className="d-flex" role="search" style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "rgba(0, 0, 0, 0.6)", pointerEvents: "none", zIndex: 1 }}>
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                  </span>
                  <input
                    className="me-2"
                    type="search"
                    placeholder="Search Stock..."
                    value={search}
                    onChange={handleSearchChange}
                    style={{
                      background: 'white',
                      border: "1px solid #d6d6d6ff",
                      borderRadius: "8px",
                      color: "#000",
                      padding: "7px 14px 7px 34px",
                      fontSize: "13px",
                      width: "280px",
                      outline: "none",
                    }}
                  />
                </form>

                {dropdown && (
                  <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, width: "420px", backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 10px 40px rgba(0,0,0,0.15)", border: "1px solid #e8edf5", zIndex: 1050, overflow: "hidden" }}>
                    <div style={{ padding: "8px 14px 6px", borderBottom: "1px solid #f1f5f9" }}>
                      <span style={{ fontSize: "10px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.8px" }}>Stock Search Results</span>
                    </div>
                    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                      {isLoading ? (
                        <div className="d-flex flex-column align-items-center justify-content-center py-4">
                          <Spinner color="primary" size="sm" />
                          <span className="text-muted small mt-2">Searching distributors...</span>
                        </div>
                      ) : (
                        <>
                          <Table bordered responsive className="mb-0" style={{ fontSize: "13px" }}>
                            <thead>
                              <tr style={{ backgroundColor: "#00509d", color: "#fff" }}>
                                <th className="py-2 border-0">Vendor</th>
                                <th className="py-2 border-0">Part</th>
                                <th className="py-2 border-0">Stock</th>
                                <th className="py-2 border-0">Price</th>
                              </tr>
                            </thead>
                            <tbody>
                              {combinedResults?.length > 0 ? (
                                combinedResults.map((row, idx) => (
                                  <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#f8fafc" }}>
                                    <td className="py-2">{row.vendor}</td>
                                    <td className="py-2">{row.part}</td>
                                    <td className="py-2">{row.stock}</td>
                                    <td className="py-2">{row.price}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr><td colSpan="4" className="text-center py-4 text-muted">No results found</td></tr>
                              )}
                            </tbody>
                          </Table>
                          {distributorsList?.length > 0 && (
                            <div style={{ padding: "10px 14px", borderTop: "1px solid #f1f5f9" }}>
                              <span style={{ fontSize: "11px", fontWeight: "700", color: "#64748b" }}>Distributors: </span>
                              <div className="d-flex flex-wrap gap-1 mt-1">
                                {distributorsList.map((dist, idx) => (
                                  <span key={idx} style={{ fontSize: "11px", fontWeight: "600", backgroundColor: "#00509d20", color: "#00509d", padding: "2px 8px", borderRadius: "10px", border: "1px solid #00509d40" }}>
                                    {dist}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {manufacturerDistributors?.length > 0 && (
                            <div style={{ padding: "10px 14px", borderTop: "1px solid #f1f5f9" }}>
                              <span style={{ fontSize: "11px", fontWeight: "700", color: "#64748b" }}>Manufacturer Distributors: </span>
                              <div className="d-flex flex-wrap gap-1 mt-1">
                                {manufacturerDistributors.map((dist, idx) => (
                                  <span key={idx} style={{ fontSize: "11px", fontWeight: "600", backgroundColor: "#ff4d4f20", color: "#ff4d4f", padding: "2px 8px", borderRadius: "10px", border: "1px solid #ff4d4f40" }}>
                                    {dist}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Tweak mobile icons wrapper */}
              <div className="navbar-action-icons d-flex align-items-center gap-3">
                {/* ---- Notifications ---- */}
                <div
                  onClick={handleNotifications}
                  className="position-relative pointer d-flex align-items-center justify-content-center"
                  title={unreadCount > 0 ? `${unreadCount} Unread Notifications` : "Notifications"}
                  style={{ width: "36px", height: "36px" }}
                >
                  <IoNotifications size={28} color="rgba(255,255,255,0.85)" />
                  {unreadCount > 0 && (
                    <span
                      className="badge rounded-pill bg-danger position-absolute"
                      style={{
                        top: "-2px",
                        right: "-2px",
                        fontSize: "10px",
                        padding: "2px 5px",
                        minWidth: "18px",
                        boxShadow: "0 0 0 2px var(--bg-primary-color, #00509d)",
                        fontWeight: "700",
                      }}
                    >
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </div>

                {/* ---- Profile ---- */}
                <ProfileDropdown
                  items={items}
                  isOpen={dropdownOpen}
                  toggleDropdown={handleProfileDropdownToggle}
                />
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default NavBar;

